import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { LuArrowRight, LuCalendarDays, LuChevronDown, LuClock } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';
import type { GuestEventSummary } from '@mds/shared/types/guest-event';
import { Button } from '@mds/shared/ui/shadcn/button';

import CardRail from '@/components/card/card-rail';
import EventCard from '@/components/card/event-card';
import MediaCard, { MediaCardRow } from '@/components/card/media-card';
import FeatureSplit from '@/components/section/feature-split';
import { FlipCalendar } from '@/components/section/free-trial-promo';
import Hero from '@/components/section/hero';
import StepShowcase from '@/components/section/step-showcase';
import { type PackageCardProps } from '@/components/card/package-card';
import PackageList from '@/components/card/package-list';
import SpaceCard from '@/components/card/space-card';
import RuledHeader from '@/components/heading/ruled-header';
import { ErrorBlock, LoadingBlock } from '@/components/states';

import { FAQ, TRIAL_WEEK } from '@/data';
import { eventQueries } from '@/features/events/events.queries';
import { galleryQueries } from '@/features/gallery/gallery.queries';
import { packageQueries } from '@/features/packages/packages.queries';
import { spaceQueries } from '@/features/spaces/spaces.queries';
import { formatEventDate, formatMoney, formatPackageUnit } from '@/lib/format';

/*
 * MISSING COMPONENT — Section
 *
 * `mx-auto max-w-page px-gutter pt-section` is repeated at the top of every
 * band on every page in this app. It wants to be one `<Section>` in
 * components/layout so the page width changes in one place, but that is a new
 * component and this pass is building with what exists. Written out for now.
 */
const band = 'mx-auto max-w-page px-gutter pt-section';

const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: 'UTC' });

/**
 * Every date from `from` to `to`, both included. Read and formatted in UTC so
 * an ISO date is the day it says on any visitor's clock, rather than the day
 * before west of Greenwich.
 */
function datesBetween(from: string, to: string) {
  const end = new Date(`${to}T00:00:00Z`);
  const dates: Date[] = [];
  for (let d = new Date(`${from}T00:00:00Z`); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    dates.push(new Date(d));
  }
  return dates;
}

/**
 * The opening week's free trial: a `FeatureSplit` with the flip calendar edge
 * to edge, and the week's days, the hours and the action in its `children`
 * slot. Gone once the offer ends (`TRIAL_WEEK.endsAt`).
 */
function TrialWeekSection() {
  if (Date.now() >= Date.parse(TRIAL_WEEK.endsAt)) return null;
  const { days, facts, cta } = TRIAL_WEEK;

  return (
    <section className={band}>
      <FeatureSplit
        media="bleed"
        imageSide="left"
        eyebrow={TRIAL_WEEK.badge}
        title={TRIAL_WEEK.title}
        titleClassName="max-sm:text-3xl"
        description={TRIAL_WEEK.description}
        visual={<FlipCalendar />}
      >
        <ol className="grid w-full grid-cols-7 gap-1.5 sm:gap-2">
          {datesBetween(days.from, days.to).map((date) => (
            <li key={date.toISOString()}>
              <time
                dateTime={date.toISOString().slice(0, 10)}
                className="flex flex-col items-center gap-0.5 rounded-input bg-on-panel/8 py-2.5"
              >
                <span className="font-mono text-2xs text-on-panel/60 uppercase">
                  {weekday.format(date)}
                </span>
                <span className="text-lg font-medium">{date.getUTCDate()}</span>
              </time>
            </li>
          ))}
        </ol>

        <ul className="grid w-full grid-cols-2 gap-x-6">
          {facts.map((fact) => (
            <li key={fact.title} className="flex flex-col gap-0.5">
              <span className="text-base font-medium">{fact.title}</span>
              <span className="text-sm text-on-panel/70">{fact.note}</span>
            </li>
          ))}
        </ul>

        {/* The default button rather than the split's own `cta`, which is the
            quiet secondary: this is the band's one action, in mint. Full width
            on a phone. */}
        <Button asChild size="lg" className="mt-2 max-sm:w-full">
          <Link to={cta.to}>{cta.label}</Link>
        </Button>
      </FeatureSplit>
    </section>
  );
}

/** The spaces, as the same card the spaces page draws — and from the same cached list. */
function SpacesSection() {
  const spaces = useQuery(spaceQueries.list());

  return (
    <section id="facilities" className={band} aria-labelledby="facilities-heading">
      <RuledHeader
        id="facilities-heading"
        title="Our spaces"
        description="A desk for the day, a meeting room for the hour, or the hall for your event."
      />

      {spaces.isPending ? (
        <LoadingBlock rows={1} />
      ) : spaces.isError ? (
        <ErrorBlock
          title="We couldn't load the spaces"
          error={spaces.error}
          onRetry={() => void spaces.refetch()}
        />
      ) : (
        <CardRail label="Our spaces">
          {spaces.data.map((space) => (
            <SpaceCard key={space.id} space={space} />
          ))}
        </CardRail>
      )}
    </section>
  );
}

/** Every desk package the API returns: rows on a phone, the design's flip cards from `lg`. */
function PackagesSection() {
  const packages = useQuery(packageQueries.list());
  // The same mapping as a space's detail page, so a package reads the same in both.
  const shown = (packages.data ?? []).map((pkg) => ({
    key: pkg.id,
    props: {
      name: pkg.name,
      price: pkg.quoteOnly ? 'By quote' : formatMoney(pkg.amountMinor),
      unit: pkg.quoteOnly
        ? ''
        : formatPackageUnit({ unit: pkg.unit, unitLabel: pkg.unitLabel ?? undefined }),
      tag: pkg.note,
      perks: pkg.benefits.map((benefit) => benefit.title),
      /* The one to pick, per the design: the Monthly Desk, by its rate-card
         key. A `featured` flag the admin app can set would be better than a
         key compared here. */
      featured: pkg.id === 'k3',
    } satisfies PackageCardProps,
  }));
  // The "Most popular" card goes second, between the first package and the
  // rest, in their API order.
  const featured = shown.find((card) => card.props.featured);
  const others = shown.filter((card) => card !== featured);
  const cards = featured ? [...others.slice(0, 1), featured, ...others.slice(1)] : shown;

  return (
    <section id="desks" className={band} aria-labelledby="desks-heading">
      <RuledHeader
        id="desks-heading"
        title="Coworking desk packages"
        description="Pay for the day, the week or the month. Every package is the same floor and the same wifi. The difference is how long the desk stays yours."
      />

      {packages.isPending ? (
        <LoadingBlock rows={1} />
      ) : packages.isError ? (
        <ErrorBlock
          title="We couldn't load the packages"
          error={packages.error}
          onRetry={() => void packages.refetch()}
        />
      ) : (
        <>
          <PackageList packages={cards} />
          {/* The same booking page a space's "Book your desk" opens, here on no
              particular floor: the visitor picks one there. */}
          <div className="mt-block flex justify-center">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/book/daily-desk" state={{ from: '/' }}>
                Book your desk
              </Link>
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * One event on its own, as a feature rather than a lone card in a rail built
 * for six: the cover beside the title, the facts and the way in. What the band
 * shows while the opening event is the only one on the calendar.
 */
function FeaturedEvent({ event }: { event: GuestEventSummary }) {
  const { schedule } = event;
  // The same two facts and icons as the event's card.
  const facts = [
    { label: formatEventDate(schedule.date), icon: LuCalendarDays },
    { label: `${schedule.startTime} – ${schedule.endTime}`, icon: LuClock },
  ];

  return (
    <FeatureSplit
      media="bleed"
      // Right, where the trial week and the café put theirs on the left, so
      // the dark cards down the page alternate sides.
      imageSide="right"
      eyebrow={event.category}
      title={event.title}
      description={event.shortDescription}
      images={event.coverImage ? [{ src: event.coverImage, alt: event.title }] : []}
      galleryLabel={event.title}
    >
      <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {facts.map(({ label, icon: Icon }) => (
          <li key={label} className="flex items-center gap-2 text-base font-medium">
            <Icon aria-hidden="true" className="size-4 shrink-0 text-brand" />
            {label}
          </li>
        ))}
      </ul>

      {/* Register is the band's one action, in mint; the details page is the
          quiet second. The list carries open events only, and the register
          page itself handles a full one (the waitlist). */}
      <div className="mt-2 flex w-full flex-wrap gap-3">
        <Button asChild size="lg" className="max-sm:w-full">
          <Link to={`/events/${event.slug}/register`}>{event.isFree ? 'Register free' : 'Register'}</Link>
        </Button>
        <Button asChild variant="secondary" size="lg" className="max-sm:w-full">
          <Link to={`/events/${event.slug}`}>See details</Link>
        </Button>
      </div>
    </FeatureSplit>
  );
}

/**
 * The coming events, laid out by how many there are: none hides the band, one
 * is featured on its own (`FeaturedEvent`), and from two the next six run in
 * the sideways rail the design gives this band.
 */
function EventsSection() {
  // The server returns open events only, soonest first.
  const events = useQuery(eventQueries.list({ limit: 6 }));

  const list = events.data?.data ?? [];

  // Nothing on the calendar: no heading over an empty rail.
  if (events.isSuccess && list.length === 0) return null;

  return (
    <section id="events" className={band} aria-labelledby="events-heading">
      <RuledHeader
        id="events-heading"
        title="Events & workshops"
        description="Learn a skill, share an idea or just come and look around. Join our open houses, workshops, roundtables and studio clinics throughout the year."
        action={
          // A text link rather than a button, in the same style as "All
          // spaces" on a space's page.
          <Link
            to="/events"
            className="group inline-flex items-center gap-2 rounded-sm font-mono text-2xs text-brand-text uppercase underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            All events
            <LuArrowRight
              aria-hidden="true"
              className="size-3.5 transition-transform duration-(--duration-fast) ease-standard group-hover:translate-x-0.5 motion-reduce:transition-none"
            />
          </Link>
        }
      />

      {events.isPending ? (
        <LoadingBlock rows={1} />
      ) : events.isError ? (
        <ErrorBlock
          title="We couldn't load the events"
          error={events.error}
          onRetry={() => void events.refetch()}
        />
      ) : list.length === 1 ? (
        <FeaturedEvent event={list[0]!} />
      ) : (
        <CardRail label="Events & workshops">
          {list.map((event) => (
            <EventCard key={event.id} event={event} />
          ))}
        </CardRail>
      )}
    </section>
  );
}

/** The café band's paragraph, the same on a phone and from `lg`. */
const CAFE_DESCRIPTION =
  'Good ideas often start in casual conversations, and the MDS Café is built for them. Every day it’s a welcoming place to meet, talk and connect. When you have something to share, the whole hall opens up for your event, with seating for a crowd, a sound system and coffee flowing all evening.';

/**
 * The café hall band: a photo and a paragraph. No prices on either layout:
 * the hall's own page carries them, one tap away.
 */
function CafeSection() {
  // The café's cover photo from the spaces list the rail above already loaded.
  // None until the café has a cover, or while the list is loading or has failed.
  const spaces = useQuery(spaceQueries.list());
  const cafe = spaces.data?.find((space) => space.slug === 'cafe');
  const images = cafe?.coverImage ? [{ src: cafe.coverImage, alt: cafe.name }] : [];

  return (
    <section id="cafe" className={band}>
      {/* On a phone, the same dark media card as the booking band. */}
      <MediaCardRow className="lg:hidden">
        <MediaCard
          variant="framed"
          dark
          images={images}
          title="More than a café"
          description={CAFE_DESCRIPTION}
          fullDescription
          footer={<PanelCardAction label="See the hall" to="/spaces/cafe" />}
        />
      </MediaCardRow>

      <FeatureSplit
        className="max-lg:hidden"
        // No eyebrow: "Yours for the evening" is in the heading now, as the
        // design sets it, and repeating it above would be the same six words
        // twice.
        title="The MDS Café: yours for the evening"
        // The phone card's paragraph, so the band says the same on every screen.
        description={CAFE_DESCRIPTION}
        images={images}
        imageSide="left"
        cta={{ label: 'See the hall', to: '/spaces/cafe' }}
      />
    </section>
  );
}

/**
 * How long a mouse rests on a question before it opens. Long enough that
 * sweeping across the list on the way somewhere else opens nothing, short
 * enough that resting on one still feels like hover and not like waiting.
 */
const HOVER_INTENT_MS = 80;

/**
 * One open at a time, and a mouse opens a question by resting on it — hover
 * does what a click does, after a short beat. Touch has no hover, so a tap is
 * the way in there, and the keyboard reaches the same toggle as the button it
 * is.
 *
 * A button and a panel rather than `<details>`: a `<details>` shows and hides
 * its content in one frame, so the card snapped open and every card under it
 * jumped. The panel here is a grid row going between `0fr` and `1fr` — the
 * one way to animate to a height nobody wrote down — so the answer slides open
 * and the list below moves with it.
 */
function FaqSection() {
  const [open, setOpen] = useState<number | null>(null);
  // The question a click just closed. Without it the next pointer move over
  // the same card would open it straight back up, and a mouse could never
  // close anything.
  const clickClosed = useRef<number | null>(null);
  // The pending hover-open, and which question it is for.
  const hoverTimer = useRef<number | undefined>(undefined);
  const hoverTarget = useRef<number | null>(null);

  const cancelHover = () => {
    window.clearTimeout(hoverTimer.current);
    hoverTarget.current = null;
  };

  useEffect(() => cancelHover, []);

  // Opening a question closes the open one. When that one sits above, its
  // answer folds away and the list rises with it, so the question just
  // picked moves up the page as its own answer opens. If that would carry it
  // up under the sticky header, the page scrolls up just enough to keep it in
  // view, alongside the animation rather than after it; otherwise nothing
  // scrolls.
  const cards = useRef<(HTMLDivElement | null)[]>([]);
  const answers = useRef<(HTMLDivElement | null)[]>([]);

  const show = (next: number | null) => {
    if (next !== null) {
      const card = cards.current[next];
      if (card) {
        const lost = open !== null && open < next ? (answers.current[open]?.offsetHeight ?? 0) : 0;
        // Where the card's top will land once the answer above has folded,
        // against the line the header leaves free (`scroll-mt-*` on the card).
        const landsAt = card.getBoundingClientRect().top - lost;
        const clear = parseFloat(getComputedStyle(card).scrollMarginTop) || 0;
        if (landsAt < clear) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          window.scrollBy({ top: landsAt - clear, behavior: reduce ? 'auto' : 'smooth' });
        }
      }
    }
    setOpen(next);
  };

  return (
    <section className={band} aria-labelledby="faq-heading">
      <RuledHeader
        id="faq-heading"
        title="Questions, answered"
        description="The things people ask at the front desk, in the order they ask them."
      />

      {/* A card per question rather than rows on one ruled list: the questions
          are a set of separate things to open, and the gap between them is what
          says so. `overflow-anchor: none` so the browser's own scroll anchoring
          does not also correct for a collapsed answer and scroll twice — the
          correction is `show`'s to make. */}
      <div className="flex flex-col gap-3 [overflow-anchor:none]">
        {FAQ.map((item, i) => {
          const isOpen = open === i;
          const questionId = `faq-question-${i}`;
          const answerId = `faq-answer-${i}`;

          return (
            <div
              key={item.question}
              ref={(node) => {
                cards.current[i] = node;
              }}
              // `pointermove`, not `pointerenter`: opening one card closes the
              // one above it, and the list shifts under a still pointer. An
              // enter event would fire on whatever slid underneath and open
              // that too; a move only fires when the hand actually moves.
              onPointerMove={(e) => {
                if (e.pointerType !== 'mouse' || clickClosed.current === i) return;
                if (isOpen || hoverTarget.current === i) return;
                cancelHover();
                hoverTarget.current = i;
                hoverTimer.current = window.setTimeout(() => {
                  hoverTarget.current = null;
                  show(i);
                }, HOVER_INTENT_MS);
              }}
              onPointerLeave={() => {
                if (hoverTarget.current === i) cancelHover();
                if (clickClosed.current === i) clickClosed.current = null;
              }}
              className={cn(
                // `scroll-mt-20`: the room the sticky header takes, which `show`
                // keeps a rising card clear of.
                'group scroll-mt-20 rounded-card border bg-surface',
                'transition-colors duration-(--duration-fast) ease-standard',
                isOpen ? 'border-brand-edge' : 'border-hairline hover:border-brand-edge/60',
              )}
            >
              <h3 className="m-0">
                <button
                  type="button"
                  id={questionId}
                  aria-expanded={isOpen}
                  aria-controls={answerId}
                  onClick={() => {
                    cancelHover();
                    clickClosed.current = isOpen ? i : null;
                    show(isOpen ? null : i);
                  }}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 px-5 py-4 text-left',
                    'text-base font-medium transition-colors duration-(--duration-fast) ease-standard',
                    // Green while it is open, and not only on hover: the card's
                    // mint edge says which one is open, and the question should
                    // still say it once the pointer has moved away.
                    isOpen ? 'text-brand-text' : 'hover:text-brand-text',
                    'rounded-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                  )}
                >
                  {/* Decoration, not content: the questions are not steps and
                      the order carries no meaning, so the number is
                      `aria-hidden` and what gets announced is the question. */}
                  <span aria-hidden="true" className="font-mono text-sm text-brand-text">
                    {i + 1}.
                  </span>
                  {item.question}
                  <LuChevronDown
                    aria-hidden="true"
                    className={cn(
                      // `ml-auto` rather than `justify-between` on the row: with
                      // three children that would space all three out and leave
                      // the number stranded away from its question.
                      'ml-auto size-5 shrink-0 text-ink-muted transition-transform',
                      'duration-(--duration-fast) ease-standard motion-reduce:transition-none',
                      isOpen && 'rotate-180',
                    )}
                  />
                </button>
              </h3>

              {/* `inert` while shut, so a collapsed answer is out of the tab
                  order and the accessibility tree, as a closed `<details>` was. */}
              <div
                ref={(node) => {
                  answers.current[i] = node;
                }}
                id={answerId}
                role="region"
                aria-labelledby={questionId}
                inert={!isOpen}
                className={cn(
                  'grid transition-[grid-template-rows,opacity] duration-(--duration-fast) ease-standard',
                  'motion-reduce:transition-none',
                  isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
                )}
              >
                {/* `min-h-0` so the row can close all the way: a grid item's
                    content height is otherwise its floor. */}
                <div className="min-h-0 overflow-hidden">
                  {/* Indented to the question rather than to the card, so the
                      answer starts under the words it answers and not under the
                      number. The measure is capped even though the card is not:
                      a line of text running the full width of the page is one
                      you lose your place in on the way back. */}
                  <p className="max-w-prose pr-5 pb-4 pl-13 text-base text-ink-secondary">
                    {item.answer}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const MEDIA_BASE_URL = import.meta.env.VITE_MEDIA_BASE_URL?.replace(/\/$/, '');

/**
 * The button closing a band's dark media card on a phone, full width. The
 * default button, which the card's `data-ground="panel"` turns mint.
 */
function PanelCardAction({ label, to }: { label: string; to: string }) {
  return (
    <div className="px-4 pt-4 pb-4 sm:px-5 sm:pb-5">
      <Button asChild size="lg" className="w-full">
        <Link to={to}>{label}</Link>
      </Button>
    </div>
  );
}

/** The booking band's steps, beside the coworking photos. */
const BOOKING_STEPS = {
  title: 'Book, attend, belong',
  description:
    'Desks, rooms and events, from your first visit to your monthly membership.',
  cta: { label: 'Explore our spaces', to: '/spaces' },
  steps: [
    {
      title: 'Book',
      description: 'Claim your desk. Start building today.',
    },
    {
      title: 'Attend',
      description: "Learn from people who've built it.",
    },
    {
      title: 'Belong',
      description: 'Build local. Grow global. Together.',
    },
  ],
};

/**
 * The booking band: the three steps typing themselves out beside the photos
 * in the "Home-images" folder of the admin file library, in name order. Staff
 * change them from the Files page — upload into the folder as Public, or
 * rename to reorder — with no deploy. Until they arrive, or with none public,
 * the panel shows its ground.
 *
 * Plain URLs, so the carousel names each photo by its position: like the About
 * strip, they are not matched to the rooms they show.
 */
function BookingSection() {
  const gallery = useQuery(galleryQueries.detail('home'));
  const photos = useMemo(() => (gallery.data ?? []).map((file) => file.contentUrl), [gallery.data]);

  return (
    <section className={band}>
      <StepShowcase
        title={BOOKING_STEPS.title}
        description={BOOKING_STEPS.description}
        steps={BOOKING_STEPS.steps}
        label="How booking works"
        images={photos}
        galleryLabel="Coworking at MDS"
        cta={BOOKING_STEPS.cta}
      />
    </section>
  );
}

/**
 * The FAQ section is hidden for now. Its code stays wired up so it comes back
 * by setting this to true.
 */
const SHOW_FAQ = false;

export default function HomePage() {
  return (
    <>
      <Hero
        title="Build Local. Grow Global."
        description="We empower ambitious individuals, startups, investors and strategic partners to connect, learn and grow, right here in Minieh."
        action={{ label: 'Browse spaces', to: '/spaces' }}
        video={MEDIA_BASE_URL && `${MEDIA_BASE_URL}/hero-video.mp4`}
      />

      <TrialWeekSection />
      {/* The booking band and the packages between the trial week and the
          events, and the spaces between the events and the café: with one
          event, all three are dark feature cards, and two never sit back to
          back. */}
      <BookingSection />
      <PackagesSection />
      <EventsSection />
      {/* Hidden for now: see SHOW_FAQ. Above the spaces so the café stays last. */}
      {SHOW_FAQ && <FaqSection />}
      <SpacesSection />
      <CafeSection />
    </>
  );
}
